import { Box } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { getFullImageUrl } from 'utils'
import { FC, Fragment, useState } from 'react'
import { ApproversInfoMessage, Person, Search } from 'components'
import { Pagination } from 'packages/eid-ui'
import { useTranslation } from 'react-i18next'
import { PersonSkeleton } from 'components/Skeletons/PersonSkeleton'
import { isNilOrEmpty } from 'packages/core'

const useStyles = makeStyles({
    paginationSteppersContainer: {
        width: '100%',
        padding: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    searchContainer: {
        maxWidth: '280px',
        padding: '22px 20px 0px 6px',
    },
})

export type StepApproversListProps = {
    totalApproversCount: number | null
    requestId?: string
    itemId?: string
    stepId: string
    useDataHook: any
}

const take = 10

export const StepApproversList: FC<StepApproversListProps> = (props) => {
    const { t } = useTranslation()

    const classes = useStyles()

    const [searchTerm, setSearchTerm] = useState('')
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(take)

    const { data, isFetching: isLoading } = props.useDataHook(
        props.itemId ? props.itemId : props.requestId,
        props?.totalApproversCount === 0 ? null : props.stepId,
        (page - 1) * perPage,
        perPage,
        searchTerm,
    )
    const approvers = data ? data.data : undefined

    const handlePageChange = (value: any) => {
        setPage(value)
    }

    const handlePageSelection = (value: any) => {
        setPage(value)
    }

    const handleItemsPerPageChange = (value: any) => {
        setPage(1)
        setPerPage(value)
    }

    const numberOfPages = data ? Math.ceil(data.totalCount / perPage) : 0

    if (isLoading || !props.stepId) {
        return <ApproversLoader />
    }

    const showSearch = approvers?.length > 0 || searchTerm.length > 0

    return (
        <Fragment>
            {showSearch && (
                <Box className={classes.searchContainer}>
                    <Search
                        fullWidth
                        outlined
                        placeholder={t('Search')}
                        value={searchTerm}
                        handleClear={(value: any) => {
                            handlePageChange(1)
                            setSearchTerm(value)
                        }}
                        handleSearch={(value: any) => {
                            if (value !== searchTerm) {
                                handlePageChange(1)
                                setSearchTerm(value)
                            }
                        }}
                    />
                </Box>
            )}

            {((approvers && approvers.length === 0) ||
                props.totalApproversCount === 0) &&
                isNilOrEmpty(searchTerm) && (
                    <Box marginY="20px" paddingX="20px">
                        <ApproversInfoMessage
                            totalCount={
                                data
                                    ? data.totalCount
                                    : props.totalApproversCount
                            }
                            visibleCount={data?.totalCount}
                        />
                    </Box>
                )}

            {isLoading && <ApproversLoader />}

            {approvers && approvers.length > 0 && (
                <Box
                    display={'flex'}
                    flexWrap={'wrap'}
                    justifyContent={'flex-start'}
                    marginTop="20px"
                >
                    {approvers.map((a: any, index: any) => (
                        <Box key={index + a.id} maxWidth="280px" marginX="8px">
                            <Person
                                size="medium"
                                name={a.friendlyName}
                                imgSrc={getFullImageUrl(a.imageThumbUrl)}
                                style={{ color: '#5d6870' }}
                            />
                        </Box>
                    ))}
                </Box>
            )}

            {numberOfPages > 1 && (
                <Box className={classes.paginationSteppersContainer}>
                    <Pagination
                        count={numberOfPages}
                        size="large"
                        page={page}
                        onChange={(_: any, value: any) =>
                            handlePageChange(value)
                        }
                        showPageSelection={true}
                        onPageSelection={handlePageSelection}
                        showItemsPerPageSelection={true}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        itemsPerPage={perPage}
                        showCountStat={true}
                        totalCount={data?.totalCount}
                    />
                </Box>
            )}
        </Fragment>
    )
}

const ApproversLoader = () => {
    return (
        <Box
            display={'flex'}
            flexWrap={'wrap'}
            justifyContent={'flex-start'}
            marginY="20px"
        >
            <Box maxWidth="280px" marginX="8px">
                <PersonSkeleton />
            </Box>

            <Box maxWidth="280px" marginX="8px">
                <PersonSkeleton />
            </Box>

            <Box maxWidth="280px" marginX="8px">
                <PersonSkeleton />
            </Box>
        </Box>
    )
}
